import React, { createContext, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Button,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

interface ModalProps {
  show: boolean;
  onHide: () => void;
  children: React.ReactNode;
  "aria-labelledby"?: string;
  dialogClassName?: string | boolean;
  backdrop?: string | boolean;
}

interface ModalHeaderProps {
  closeButton?: boolean;
  children: React.ReactNode;
}

interface ModalTitleProps {
  id?: string;
  children: React.ReactNode;
}

interface ModalBodyProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface ModalFooterProps {
  children: React.ReactNode;
}

interface ModalDismissProps {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

// Create context to pass onHide function down to child components
const ModalContext = createContext<{
  onHide: () => void;
} | null>(null);

const Modal: React.FC<ModalProps> & {
  Header: React.FC<ModalHeaderProps>;
  Title: React.FC<ModalTitleProps>;
  Body: React.FC<ModalBodyProps>;
  Footer: React.FC<ModalFooterProps>;
  Dismiss: React.FC<ModalDismissProps>;
} = ({ show, onHide, children, ...rest }) => {
  return (
    <ModalContext.Provider value={{ onHide }}>
      <Dialog open={show} onClose={onHide} maxWidth="sm" fullWidth {...rest}>
        {children}
      </Dialog>
    </ModalContext.Provider>
  );
};

const ModalHeader: React.FC<ModalHeaderProps> = ({ closeButton, children }) => {
  const context = useContext(ModalContext);

  return (
    <DialogTitle>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>{children}</Box>
        {closeButton && context && (
          <IconButton aria-label="close" onClick={context.onHide} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </DialogTitle>
  );
};

const ModalTitle: React.FC<ModalTitleProps> = ({ id, children }) => {
  return (
    <Typography variant="h6" component="h2" id={id}>
      {children}
    </Typography>
  );
};

const ModalBody: React.FC<ModalBodyProps> = ({ children, style }) => {
  return <DialogContent style={style}>{children}</DialogContent>;
};

const ModalFooter: React.FC<ModalFooterProps> = ({ children }) => {
  return <DialogActions>{children}</DialogActions>;
};

const ModalDismiss: React.FC<ModalDismissProps> = ({
  className,
  children,
  onClick,
  disabled,
}) => {
  const context = useContext(ModalContext);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (context) {
      context.onHide();
    }
  };

  return (
    <Button
      className={className}
      onClick={handleClick}
      type="button"
      disabled={disabled}
    >
      {children}
    </Button>
  );
};

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Dismiss = ModalDismiss;

export default Modal;
